import axios from '../config/axiosConfig';

const blueTeamService = {
  // Get all findings
  async getFindings(filters = {}) {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);
    if (filters.status) params.append('status', filters.status);
    if (filters.severity) params.append('severity', filters.severity);
    if (filters.source) params.append('source', filters.source);
    if (filters.limit) params.append('limit', filters.limit);

    const response = await axios.get(`/blueteam/findings?${params.toString()}`);
    return response.data;
  },

  // Get findings for a project
  async getFindingsByProject(projectId) {
    const response = await axios.get(`/blueteam/findings/project/${projectId}`);
    return response.data;
  },

  // Get single finding
  async getFinding(findingId) {
    const response = await axios.get(`/blueteam/findings/${findingId}`);
    return response.data;
  },

  // Create new finding
  async createFinding(findingData) {
    const response = await axios.post(`/blueteam/findings`, findingData);
    return response.data;
  },

  // Update finding
  async updateFinding(findingId, findingData) {
    const response = await axios.put(`/blueteam/findings/${findingId}`, findingData);
    return response.data;
  },

  // Delete finding
  async deleteFinding(findingId) {
    const response = await axios.delete(`/blueteam/findings/${findingId}`);
    return response.data;
  },

  // Correlate finding with action
  async correlateFindingWithAction(findingId, actionId) {
    const response = await axios.post(`/blueteam/findings/${findingId}/correlate/${actionId}`);
    return response.data;
  },

  // Remove correlation from a finding
  async uncorrelateFinding(findingId) {
    const response = await axios.post(`/blueteam/findings/${findingId}/uncorrelate`);
    return response.data;
  },

  // Get project statistics
  async getProjectStats(projectId) {
    const response = await axios.get(`/blueteam/stats/project/${projectId}`);
    return response.data;
  },

  // Get unmatched findings
  async getUnmatchedFindings(projectId) {
    const response = await axios.get(`/blueteam/unmatched/project/${projectId}`);
    return response.data;
  },

  // Get correlation suggestions
  async getCorrelationSuggestions(projectId) {
    const response = await axios.get(`/blueteam/correlation/suggestions/${projectId}`);
    return response.data;
  },

  // Get combined timeline
  async getTimeline(projectId) {
    const response = await axios.get(`/blueteam/timeline/project/${projectId}`);
    return response.data;
  },

  // Get detection rate statistics
  async getDetectionRate(projectId) {
    const response = await axios.get(`/blueteam/detection-rate/project/${projectId}`);
    return response.data;
  },

  // Bulk auto-correlate
  async bulkCorrelate(projectId, minScore = 50) {
    const response = await axios.post(`/blueteam/bulk-correlate/project/${projectId}`, { minScore });
    return response.data;
  },

  // Get SIEM-imported findings for review
  async getSIEMReviewFindings(projectId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.targetMatchOnly) params.append('targetMatchOnly', 'true');
    if (filters.severity) params.append('severity', filters.severity);
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.reviewStatus) params.append('reviewStatus', filters.reviewStatus);

    const response = await axios.get(`/blueteam/siem-review/project/${projectId}?${params.toString()}`);
    return response.data;
  },

  // Update review status for a single finding
  async updateSIEMReviewStatus(findingId, siemReviewStatus) {
    const response = await axios.put(`/blueteam/siem-review/${findingId}`, { siemReviewStatus });
    return response.data;
  },

  // Bulk update review status for multiple findings
  async bulkUpdateSIEMReviewStatus(findingIds, siemReviewStatus) {
    const response = await axios.put(`/blueteam/siem-review/bulk-update`, { findingIds, siemReviewStatus });
    return response.data;
  },

  // Seed sample blue team findings for a project
  async seedFindings(projectId) {
    const response = await axios.post(`/blueteam/seed/${projectId}`);
    return response.data;
  },

  // Detection sources enum
  DETECTION_SOURCES: [
    { value: 'siem', label: 'SIEM' },
    { value: 'edr', label: 'EDR' },
    { value: 'ndr', label: 'NDR' },
    { value: 'ids', label: 'IDS/IPS' },
    { value: 'firewall', label: 'Firewall' },
    { value: 'manual', label: 'Manual Analysis' },
    { value: 'honeypot', label: 'Honeypot' },
    { value: 'log_analysis', label: 'Log Analysis' },
    { value: 'user_report', label: 'User Report' },
    { value: 'other', label: 'Other' }
  ],

  // Severity levels
  SEVERITY_LEVELS: [
    { value: 'critical', label: 'Critical', color: 'red' },
    { value: 'high', label: 'High', color: 'orange' },
    { value: 'medium', label: 'Medium', color: 'yellow' },
    { value: 'low', label: 'Low', color: 'blue' },
    { value: 'informational', label: 'Informational', color: 'gray' }
  ],

  // Status options
  STATUS_OPTIONS: [
    { value: 'new', label: 'New' },
    { value: 'investigating', label: 'Investigating' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'false_positive', label: 'False Positive' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'escalated', label: 'Escalated' }
  ],

  // IOC types
  IOC_TYPES: [
    { value: 'ip', label: 'IP Address' },
    { value: 'domain', label: 'Domain' },
    { value: 'hash_md5', label: 'MD5 Hash' },
    { value: 'hash_sha1', label: 'SHA1 Hash' },
    { value: 'hash_sha256', label: 'SHA256 Hash' },
    { value: 'url', label: 'URL' },
    { value: 'email', label: 'Email Address' },
    { value: 'file_path', label: 'File Path' },
    { value: 'registry_key', label: 'Registry Key' },
    { value: 'mutex', label: 'Mutex' },
    { value: 'other', label: 'Other' }
  ]
};

export default blueTeamService;
